/** Script & voice-config queries. */

import { type UseQueryResult, useQueries, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { queryKeys } from '../query-keys';
import { useApiClient } from '../react/ApiClientProvider';
import {
  type HotelScript,
  hotelScriptSchema,
  normalizeVoiceConfig,
  type VoiceConfig,
  voiceConfigSchema,
} from '../responses';
import { unwrap } from './_helpers';

/** `GET /scripts/versions?language=&field=` — version history for one script field. */
export function useScriptVersions(
  tenantId: string,
  hotelId: string,
  language: string,
  field: string,
): UseQueryResult<HotelScript[]> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.scriptVersions(tenantId, hotelId, { language, field }),
    queryFn: async () => {
      const result = await client.GET(
        '/api/v1/tenants/{tenantId}/hotels/{hotelId}/scripts/versions',
        { params: { path: { tenantId, hotelId }, query: { language, field } }, parseAs: 'json' },
      );
      return unwrap(result, z.array(hotelScriptSchema));
    },
    enabled: language.length > 0 && field.length > 0,
  });
}

/** Per-field version history for one language: `{ [field]: HotelScript[] }`, newest-first. */
export interface ScriptVersionsByLanguage {
  byField: Record<string, HotelScript[]>;
  isPending: boolean;
  isError: boolean;
  isSuccess: boolean;
}

/**
 * Version history for every `field` in one `language`, fanned out over the per-field
 * `GET /scripts/versions` endpoint (which requires both `language` and `field`).
 *
 * The Script Studio needs the TRUE status of each field — including drafts, which the
 * approved-only `/voice-config` payload omits — so the display status per field is the
 * latest version's status (a newer draft beats an older approved). `fields` is a stable
 * constant list, so the query count is fixed across renders.
 */
export function useScriptVersionsByLanguage(
  tenantId: string,
  hotelId: string,
  language: string,
  fields: readonly string[],
): ScriptVersionsByLanguage {
  const client = useApiClient();
  return useQueries({
    queries: fields.map((field) => ({
      queryKey: queryKeys.scriptVersions(tenantId, hotelId, { language, field }),
      queryFn: async () => {
        const result = await client.GET(
          '/api/v1/tenants/{tenantId}/hotels/{hotelId}/scripts/versions',
          { params: { path: { tenantId, hotelId }, query: { language, field } }, parseAs: 'json' },
        );
        return unwrap(result, z.array(hotelScriptSchema));
      },
      enabled: language.length > 0,
    })),
    combine: (results) => {
      const byField: Record<string, HotelScript[]> = {};
      fields.forEach((field, i) => {
        byField[field] = results[i]?.data ?? [];
      });
      return {
        byField,
        isPending: results.some((r) => r.isPending),
        isError: results.some((r) => r.isError),
        isSuccess: results.every((r) => r.isSuccess),
      };
    },
  });
}

/** `GET /voice-config` — the hotel's voice config (fallback language, handoff rules, scripts). */
export function useVoiceConfig(tenantId: string, hotelId: string): UseQueryResult<VoiceConfig> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.voiceConfig(tenantId, hotelId),
    queryFn: async () => {
      const result = await client.GET('/api/v1/tenants/{tenantId}/hotels/{hotelId}/voice-config', {
        params: { path: { tenantId, hotelId } },
        parseAs: 'json',
      });
      normalizeVoiceConfig(result.data);
      return unwrap(result, voiceConfigSchema);
    },
  });
}
